import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useUser } from '../../context/UserContext';
import { useLocation } from 'react-router-dom';
import api from '../../api/api';
import './hostCleanerManager.css';

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;
const personName = (person) => person?.display || person?.email || 'Cleaner';
const comboKey = (beds, baths) => `${Number(beds || 0)}:${Number(baths || 0)}`;

const HostCleanerManager = () => {
    const { user } = useUser();
    const location = useLocation();
    const [cleaners, setCleaners] = useState([]);
    const [hosts, setHosts] = useState([]);
    const [properties, setProperties] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [sharesByCleaner, setSharesByCleaner] = useState({});
    const [selectedCleanerByJob, setSelectedCleanerByJob] = useState({});
    const [requestsByJob, setRequestsByJob] = useState({});
    const [newCleaner, setNewCleaner] = useState({ display: '', email: '', phone: '', customMessage: '', canClaim: false, shareHostIds: [], rates: {} });
    const [editing, setEditing] = useState({});
    const [rateDrafts, setRateDrafts] = useState({});
    const [shareDrafts, setShareDrafts] = useState({});
    const [showCreate, setShowCreate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [cleanerRes, hostRes, propertyRes, jobRes] = await Promise.all([
                api.get('/cleaner-workforce'),
                api.get('/cleaner-workforce/hosts'),
                api.get('/cleaner-workforce/properties'),
                api.get('/cleaner-workforce/jobs')
            ]);
            const nextCleaners = Array.isArray(cleanerRes.data) ? cleanerRes.data : [];
            setCleaners(nextCleaners);
            setHosts(Array.isArray(hostRes.data) ? hostRes.data : []);
            setProperties(Array.isArray(propertyRes.data) ? propertyRes.data : []);
            setJobs(Array.isArray(jobRes.data) ? jobRes.data : []);
            setRateDrafts(Object.fromEntries(nextCleaners.map((cleaner) => [cleaner.id, Object.fromEntries((cleaner.rates || []).map((rate) => [comboKey(rate.bedrooms, rate.bathrooms), String(rate.rate)]))])));
            const shares = await Promise.all(nextCleaners.map(async (cleaner) => {
                try {
                    const response = await api.get(`/cleaner-workforce/${cleaner.id}/shares`);
                    return [cleaner.id, Array.isArray(response.data) ? response.data : []];
                } catch (_) {
                    return [cleaner.id, []];
                }
            }));
            setSharesByCleaner(Object.fromEntries(shares));
            const jobIds = (Array.isArray(jobRes.data) ? jobRes.data : []).filter((job) => ['open', 'offered'].includes(job.status)).map((job) => Number(job.id));
            const requests = await Promise.all(jobIds.map(async (jobId) => {
                try {
                    const response = await api.get(`/cleaner-workforce/jobs/${jobId}/requests`);
                    return [jobId, response.data || []];
                } catch (_) {
                    return [jobId, []];
                }
            }));
            setRequestsByJob(Object.fromEntries(requests));
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to load cleaner workspace.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role === 'host') loadData();
    }, [loadData, user?.role]);

    useEffect(() => {
        const jobId = new URLSearchParams(location.search).get('cleaningJobId');
        if (!jobId || loading) return;
        const target = document.getElementById(`cleaning-job-${jobId}`);
        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            target.classList.add('cleaner-job-highlight');
        }
    }, [location.search, loading, jobs]);

    const combinations = useMemo(() => {
        const values = new Map();
        properties.forEach((property) => {
            const bedrooms = Number(property.bedrooms || 0);
            const bathrooms = Number(property.bathrooms || 0);
            const key = comboKey(bedrooms, bathrooms);
            if (!values.has(key)) values.set(key, { key, bedrooms, bathrooms });
        });
        return Array.from(values.values()).sort((left, right) => left.bedrooms - right.bedrooms || left.bathrooms - right.bathrooms);
    }, [properties]);

    const updateCreateRate = (key, value) => setNewCleaner((current) => ({ ...current, rates: { ...current.rates, [key]: value } }));
    const updateRateDraft = (cleanerId, key, value) => setRateDrafts((current) => ({ ...current, [cleanerId]: { ...(current[cleanerId] || {}), [key]: value } }));

    const createCleaner = async (event) => {
        event.preventDefault();
        setSaving('create');
        setError('');
        setNotice('');
        try {
            const response = await api.post('/cleaner-workforce', {
                display: newCleaner.display,
                email: newCleaner.email,
                phone: newCleaner.phone,
                customMessage: newCleaner.customMessage
            });
            const cleanerId = Number(response.data.cleanerId);
            const rates = combinations.filter((combo) => newCleaner.rates[combo.key] !== undefined && newCleaner.rates[combo.key] !== '')
                .map((combo) => ({ bedrooms: combo.bedrooms, bathrooms: combo.bathrooms, rate: Number(newCleaner.rates[combo.key]) }));
            if (rates.length) await api.put(`/cleaner-workforce/${cleanerId}/rates`, { rates });
            await api.put(`/cleaner-workforce/${cleanerId}/claim-permission`, { canClaim: newCleaner.canClaim });
            await Promise.all(newCleaner.shareHostIds.map((hostUserId) => api.put(`/cleaner-workforce/${cleanerId}/share`, { hostUserId: Number(hostUserId), canClaim: false })));
            setNotice(response.data.invited
                ? response.data.invitationSent ? 'Cleaner created. Password setup instructions were emailed.' : 'Cleaner profile saved, but the setup email could not be sent. Use Resend setup email below.'
                : 'Cleaner linked and shared successfully.');
            setNewCleaner({ display: '', email: '', phone: '', customMessage: '', canClaim: false, shareHostIds: [], rates: {} });
            setShowCreate(false);
            await loadData();
        } catch (requestError) {
            await loadData();
            setError(requestError?.response?.data?.error || 'Unable to create cleaner.');
        } finally {
            setSaving('');
        }
    };

    const saveProfile = async (cleanerId) => {
        setSaving(`profile-${cleanerId}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/${cleanerId}/profile`, editing[cleanerId]);
            setEditing((current) => { const next = { ...current }; delete next[cleanerId]; return next; });
            await loadData();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to update cleaner details.');
        } finally { setSaving(''); }
    };

    const saveRates = async (cleanerId) => {
        setSaving(`rates-${cleanerId}`);
        setError('');
        try {
            const rates = combinations.filter((combo) => rateDrafts[cleanerId]?.[combo.key] !== undefined && rateDrafts[cleanerId]?.[combo.key] !== '')
                .map((combo) => ({ bedrooms: combo.bedrooms, bathrooms: combo.bathrooms, rate: Number(rateDrafts[cleanerId][combo.key]) }));
            await api.put(`/cleaner-workforce/${cleanerId}/rates`, { rates });
            setNotice('Rates saved for this host.');
            await loadData();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to save cleaner rates.');
        } finally { setSaving(''); }
    };

    const saveShare = async (cleanerId, hostUserId, canClaim) => {
        setSaving(`share-${cleanerId}-${hostUserId}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/${cleanerId}/share`, { hostUserId: Number(hostUserId), canClaim });
            const response = await api.get(`/cleaner-workforce/${cleanerId}/shares`);
            setSharesByCleaner((current) => ({ ...current, [cleanerId]: response.data || [] }));
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to update cleaner sharing.');
        } finally { setSaving(''); }
    };

    const resendInvitation = async (cleanerId) => {
        setSaving(`invite-${cleanerId}`);
        setError('');
        try {
            await api.post(`/cleaner-workforce/${cleanerId}/invite`, {});
            setNotice('Password setup email sent.');
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to send setup email.');
        } finally { setSaving(''); }
    };

    const removeShare = async (cleanerId, hostUserId) => {
        if (!window.confirm('Remove this host’s access to the cleaner? Existing cleaning records will remain.')) return;
        setSaving(`share-${cleanerId}-${hostUserId}`);
        try {
            await api.delete(`/cleaner-workforce/${cleanerId}/share/${hostUserId}`);
            const response = await api.get(`/cleaner-workforce/${cleanerId}/shares`);
            setSharesByCleaner((current) => ({ ...current, [cleanerId]: response.data || [] }));
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to remove host access.');
        } finally { setSaving(''); }
    };

    const assignCleaner = async (jobId) => {
        const cleanerId = Number(selectedCleanerByJob[jobId]);
        if (!cleanerId) return;
        setSaving(`job-${jobId}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/jobs/${jobId}/assign`, { cleanerId });
            setNotice('Assignment offer sent. The cleaner was notified by email and in-app alert.');
            await loadData();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to assign cleaner.');
        } finally { setSaving(''); }
    };

    const reviewClaim = async (claimId, decision) => {
        setSaving(`claim-${claimId}`);
        setError('');
        try {
            await api.put(`/cleaner-workforce/claims/${claimId}/${decision}`, {});
            setNotice(decision === 'approve' ? 'Claim approved and cleaner notified.' : 'Claim declined and cleaner notified.');
            await loadData();
        } catch (requestError) {
            setError(requestError?.response?.data?.error || 'Unable to review cleaner request.');
        } finally { setSaving(''); }
    };

    if (user?.role !== 'host') return null;

    return (
        <section className="cleaner-manager">
            <header className="cleaner-manager-header">
                <div><p className="cleaner-manager-kicker">Private workforce</p><h2>My Cleaners</h2><p>Cleaner access and rates are private to your host account unless you share them.</p></div>
                <button type="button" className="btn-primary" onClick={() => setShowCreate((value) => !value)}>+ Create new cleaner</button>
            </header>
            {error && <div className="cleaner-manager-message cleaner-manager-error" role="alert">{error}</div>}
            {notice && <div className="cleaner-manager-message cleaner-manager-success" role="status">{notice}</div>}

            {showCreate && (
                <form className="cleaner-editor" onSubmit={createCleaner}>
                    <div className="cleaner-editor-heading"><h3>Create cleaner login</h3><button type="button" className="cleaner-dismiss" onClick={() => setShowCreate(false)}>Close</button></div>
                    <p>New cleaners receive an email link to set their password. Existing host accounts keep their role and gain a cleaner profile.</p>
                    <div className="cleaner-form-grid">
                        <label>Name<input required value={newCleaner.display} onChange={(event) => setNewCleaner((current) => ({ ...current, display: event.target.value }))} /></label>
                        <label>Email<input required type="email" value={newCleaner.email} onChange={(event) => setNewCleaner((current) => ({ ...current, email: event.target.value }))} /></label>
                        <label>Phone (optional)<input type="tel" value={newCleaner.phone} onChange={(event) => setNewCleaner((current) => ({ ...current, phone: event.target.value }))} /></label>
                    </div>
                    <label className="cleaner-invite-message">Message for the cleaner (optional)<textarea rows="4" maxLength={2000} value={newCleaner.customMessage} onChange={(event) => setNewCleaner((current) => ({ ...current, customMessage: event.target.value }))} placeholder="Share expectations, rates per unit type, or other details…" /></label>
                    <h4>Rates for your properties</h4>
                    {combinations.length ? <div className="cleaner-rate-grid">
                        {combinations.map((combo) => <label key={combo.key}>{combo.bedrooms} bed / {combo.bathrooms} bath ($)<input type="number" min="0" step="0.01" value={newCleaner.rates[combo.key] || ''} onChange={(event) => updateCreateRate(combo.key, event.target.value)} placeholder="Rate" /></label>)}
                    </div> : <p>Add a property with bed/bath counts to configure rates.</p>}
                    <h4>Share with hosts</h4>
                    <div className="cleaner-share-picker">
                        {hosts.length ? hosts.map((host) => <label key={host.id}><input type="checkbox" checked={newCleaner.shareHostIds.includes(Number(host.id))} onChange={(event) => setNewCleaner((current) => ({ ...current, shareHostIds: event.target.checked ? [...current.shareHostIds, Number(host.id)] : current.shareHostIds.filter((id) => id !== Number(host.id)) }))} />{personName(host)}</label>) : <span>No other hosts currently share a property with you.</span>}
                    </div>
                    <label className="cleaner-claim-toggle"><input type="checkbox" checked={newCleaner.canClaim} onChange={(event) => setNewCleaner((current) => ({ ...current, canClaim: event.target.checked }))} />Allow this cleaner to claim my open jobs directly</label>
                    <div className="cleaner-editor-actions"><button type="button" className="btn-secondary" onClick={() => setShowCreate(false)}>Cancel</button><button className="btn-primary" disabled={saving === 'create'}>{saving === 'create' ? 'Creating...' : 'Create cleaner'}</button></div>
                </form>
            )}

            {loading ? <p className="cleaner-manager-empty">Loading cleaner workspace…</p> : cleaners.length === 0 ? <p className="cleaner-manager-empty">No individual cleaners yet. Create a cleaner to start assigning booking cleanings.</p> : (
                <div className="cleaner-manager-list">
                    {cleaners.map((cleaner) => {
                        const profile = cleaner.user || {};
                        const shares = sharesByCleaner[cleaner.id] || [];
                        const draft = editing[cleaner.id] || { display: profile.display || '', email: profile.email || '', phone: profile.phone || '' };
                        return <article className="cleaner-manager-card" key={cleaner.id}>
                            <div className="cleaner-card-heading"><div><h3>{profile.display || profile.email}</h3><p>{profile.email}{profile.phone ? ` · ${profile.phone}` : ''}</p></div><div className="cleaner-card-actions"><button type="button" className="cleaner-text-button" onClick={() => setEditing((current) => ({ ...current, [cleaner.id]: { display: profile.display || '', email: profile.email || '', phone: profile.phone || '' } }))}>Edit details</button>{profile.role === 'cleaner' && <button type="button" className="cleaner-text-button" disabled={saving === `invite-${cleaner.id}`} onClick={() => resendInvitation(cleaner.id)}>Resend setup email</button>}</div></div>
                            {editing[cleaner.id] && <div className="cleaner-form-grid cleaner-inline-editor">
                                <label>Name<input value={draft.display} onChange={(event) => setEditing((current) => ({ ...current, [cleaner.id]: { ...current[cleaner.id], display: event.target.value } }))} /></label>
                                <label>Email<input type="email" value={draft.email} onChange={(event) => setEditing((current) => ({ ...current, [cleaner.id]: { ...current[cleaner.id], email: event.target.value } }))} /></label>
                                <label>Phone<input type="tel" value={draft.phone || ''} onChange={(event) => setEditing((current) => ({ ...current, [cleaner.id]: { ...current[cleaner.id], phone: event.target.value } }))} /></label>
                                <div className="cleaner-editor-actions"><button type="button" className="btn-secondary" onClick={() => setEditing((current) => { const next = { ...current }; delete next[cleaner.id]; return next; })}>Cancel</button><button type="button" className="btn-primary" onClick={() => saveProfile(cleaner.id)} disabled={saving === `profile-${cleaner.id}`}>Save</button></div>
                            </div>}
                            <h4>Rates (this host)</h4>
                            {combinations.length ? <div className="cleaner-rate-grid">
                                {combinations.map((combo) => <label key={combo.key}>{combo.bedrooms} bed / {combo.bathrooms} bath ($)<input type="number" min="0" step="0.01" value={rateDrafts[cleaner.id]?.[combo.key] || ''} onChange={(event) => updateRateDraft(cleaner.id, combo.key, event.target.value)} placeholder="Rate" /></label>)}
                            </div> : <p>No property bed/bath combinations found.</p>}
                            <button type="button" className="btn-secondary cleaner-section-save" onClick={() => saveRates(cleaner.id)} disabled={saving === `rates-${cleaner.id}`}>{saving === `rates-${cleaner.id}` ? 'Saving…' : 'Save rates'}</button>
                            <h4>Shared hosts and claim permissions</h4>
                            <div className="cleaner-shares">
                                {shares.map((share) => <div className="cleaner-share-row" key={share.id}>
                                    <strong>{share.hostUserId === Number(user.id) ? `${personName(share.host)} (you)` : personName(share.host)}</strong>
                                    <label><input type="checkbox" checked={Boolean(share.canClaim)} onChange={(event) => saveShare(cleaner.id, share.hostUserId, event.target.checked)} />Can claim jobs</label>
                                    {share.hostUserId !== Number(user.id) && <button type="button" className="cleaner-text-button cleaner-revoke" onClick={() => removeShare(cleaner.id, share.hostUserId)}>Remove</button>}
                                </div>)}
                            </div>
                            <div className="cleaner-add-share">
                                <select value={shareDrafts[cleaner.id] || ''} onChange={(event) => setShareDrafts((current) => ({ ...current, [cleaner.id]: event.target.value }))}>
                                    <option value="">Share with a host…</option>
                                    {hosts.filter((host) => !shares.some((share) => Number(share.hostUserId) === Number(host.id))).map((host) => <option key={host.id} value={host.id}>{personName(host)}</option>)}
                                </select>
                                <button type="button" className="btn-secondary" disabled={!shareDrafts[cleaner.id]} onClick={async () => { await saveShare(cleaner.id, shareDrafts[cleaner.id], false); setShareDrafts((current) => ({ ...current, [cleaner.id]: '' })); }}>Share</button>
                            </div>
                        </article>;
                    })}
                </div>
            )}

            <section className="cleaner-jobs-section">
                <header><div><p className="cleaner-manager-kicker">Booking workflow</p><h2>Cleaning jobs</h2></div><span>{jobs.length} jobs</span></header>
                {jobs.length === 0 ? <p className="cleaner-manager-empty">No booking-linked cleanings in your properties yet.</p> : <div className="cleaner-job-list">
                    {jobs.map((job) => <article className="cleaner-job-card" id={`cleaning-job-${job.id}`} key={job.id}>
                        <div className="cleaner-job-info"><strong>{job.property?.name || 'Property'}</strong><span>{job.checkoutDate} · {job.property?.bedrooms || 0} bed / {job.property?.bathrooms || 0} bath</span></div>
                        <span className={`cleaner-job-status cleaner-job-status-${job.status}`}>{job.status}</span>
                        {job.status === 'open' && job.offeredCleaner ? <div className="cleaner-offer-status">Offer pending with <strong>{personName(job.offeredCleaner)}</strong>. Other eligible cleaners may still claim this job.</div> : job.status === 'open' ? <div className="cleaner-job-assign">
                            <select value={selectedCleanerByJob[job.id] || ''} onChange={(event) => setSelectedCleanerByJob((current) => ({ ...current, [job.id]: event.target.value }))}>
                                <option value="">Select cleaner…</option>
                                {cleaners.map((cleaner) => <option key={cleaner.id} value={cleaner.id}>{personName(cleaner.user)}</option>)}
                            </select>
                            <button type="button" className="btn-primary" disabled={!selectedCleanerByJob[job.id] || saving === `job-${job.id}`} onClick={() => assignCleaner(job.id)}>Offer assignment</button>
                        </div> : <span className="cleaner-job-info">{job.assignedCleaner ? `${personName(job.assignedCleaner)} · ${job.status}${job.agreedRate == null ? '' : ` · ${money(job.agreedRate)}`}` : job.status}</span>}
                        {(requestsByJob[job.id] || []).filter((request) => request.status === 'pending').map((request) => <div className="cleaner-claim-row" key={request.id}>
                            <strong>{personName(request.cleaner)} requested this job</strong>
                            <button type="button" className="btn-secondary" disabled={saving === `claim-${request.id}`} onClick={() => reviewClaim(request.id, 'decline')}>Decline</button>
                            <button type="button" className="btn-primary" disabled={saving === `claim-${request.id}`} onClick={() => reviewClaim(request.id, 'approve')}>Approve</button>
                        </div>)}
                    </article>)}
                </div>}
            </section>

        </section>
    );
};

export default HostCleanerManager;
